import React from "react";
import "./style.scss";

type Props = React.ThHTMLAttributes<HTMLTableCellElement> & {
    resizeKey?: string;
    minWidth?: number;
    /** Độ rộng thực tế của mọi cột resize được tại lúc bắt đầu kéo */
    onColResizeStart?: (measured: Record<string, number>) => void;
    onColResize?: (key: string, width: number) => void;
    onColResizeEnd?: (key: string, width: number, measured: Record<string, number>) => void;
};

/**
 * Header cell của antd Table có tay kéo ở mép phải để đổi độ rộng cột.
 * Chỉ hiện tay kéo khi cột được useTableColumnSettings gắn props resize.
 */
const ResizableHeaderCell: React.FC<Props> = ({
    resizeKey,
    minWidth = 60,
    onColResizeStart,
    onColResize,
    onColResizeEnd,
    ...rest
}) => {
    if (!resizeKey || !onColResize) return <th {...rest} />;

    const handleMouseDown = (e: React.MouseEvent<HTMLSpanElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const th = e.currentTarget.parentElement as HTMLElement;
        const startX = e.clientX;
        const startWidth = th.getBoundingClientRect().width;
        let width = startWidth;

        // Chốt độ rộng hiện tại của mọi cột: bảng chuyển sang layout fixed mà không bị nhảy
        const measured: Record<string, number> = {};
        th.parentElement?.querySelectorAll<HTMLElement>("th[data-resize-key]").forEach((cell) => {
            measured[cell.dataset.resizeKey!] = Math.round(cell.getBoundingClientRect().width);
        });
        // Chỉ coi là kéo khi chuột thực sự di chuyển (click nhầm vào mép không đổi gì)
        let started = false;

        const onMove = (ev: MouseEvent) => {
            if (!started) {
                started = true;
                onColResizeStart?.(measured);
            }
            width = Math.max(minWidth, startWidth + ev.clientX - startX);
            onColResize(resizeKey, width);
        };
        const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
            document.body.classList.remove("col-resizing");
            if (started) onColResizeEnd?.(resizeKey, width, measured);
        };

        document.body.classList.add("col-resizing");
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    return (
        <th {...rest} data-resize-key={resizeKey} className={`${rest.className || ""} col-resizable-th`}>
            {rest.children}
            <span
                className="col-resize-handle"
                onMouseDown={handleMouseDown}
                // Không để click vào tay kéo kích hoạt sort của cột
                onClick={(e) => e.stopPropagation()}
            />
        </th>
    );
};

export default ResizableHeaderCell;
